import { z } from "zod";

/**
 * The built-in areas a routine or a skill belongs to: what kind of work it
 * does. One area each. The web pages group by area, in this order — the work
 * areas first, then the areas for okthx's general capabilities and for okthx
 * itself.
 *
 * An id is stable once shipped: routines, skills and a user's own items store
 * it. The name is display copy and can change.
 */
export const ROUTINE_AREA_IDS = [
  "briefings",
  "inbox",
  "meetings",
  "calendar",
  "follow-ups",
  "customers",
  "sales",
  "money",
  "purchasing",
  "documents",
  "marketing",
  "contacts",
  "team",
  "projects",
  "markets",
  "engineering",
  "research",
  "travel-and-errands",
  "personal",
  "tools",
  "okthx",
] as const;

export const routineAreaIdSchema = z.enum(ROUTINE_AREA_IDS);
export type RoutineAreaId = z.infer<typeof routineAreaIdSchema>;

const ROUTINE_AREA_NAMES: Record<RoutineAreaId, string> = {
  briefings: "Briefings",
  inbox: "Inbox",
  meetings: "Meetings",
  calendar: "Calendar",
  "follow-ups": "Follow-ups",
  customers: "Customers",
  sales: "Sales",
  money: "Money",
  purchasing: "Purchasing",
  documents: "Documents",
  marketing: "Marketing",
  contacts: "Contacts",
  team: "Team",
  projects: "Projects",
  markets: "Markets",
  engineering: "Engineering",
  research: "Research",
  "travel-and-errands": "Travel and errands",
  personal: "Personal",
  tools: "Tools",
  okthx: "Okthx",
};

export const ROUTINE_AREAS: readonly { id: RoutineAreaId; name: string }[] = ROUTINE_AREA_IDS.map(
  (id) => ({ id, name: ROUTINE_AREA_NAMES[id] }),
);

/** The longest custom area name a user's routine or skill can carry. */
export const USER_AREA_MAX_LENGTH = 40;

/** The built-in area id that `value` names by id or display name, ignoring case. */
export function builtInAreaIdFor(value: string): RoutineAreaId | undefined {
  const lower = value.toLowerCase();
  return ROUTINE_AREAS.find((a) => a.id === lower || a.name.toLowerCase() === lower)?.id;
}

/**
 * A user's area name as it is stored: trimmed, with angle brackets, control and
 * format characters (a zero-width space, a NUL) removed and whitespace runs
 * collapsed, because the name reaches the agent's prompt and must not look
 * empty on the page. Shared so a form can show, and refuse, what the api will
 * make of a name before it sends it.
 */
export const cleanUserArea = (s: string): string =>
  // Whitespace first, so a tab or a newline between words becomes a space
  // rather than being removed with the other control characters; and again
  // after, so a removed character between two spaces leaves one space.
  s
    .replace(/\s+/g, " ")
    .replace(/[<>\p{Cc}\p{Cf}]/gu, "")
    .replace(/ {2,}/g, " ")
    .trim();

/**
 * The area on a user's own routine or skill: a built-in area id, or a name the
 * user added, cleaned by `cleanUserArea`. A value that names a built-in area by
 * its id or its display name, in any case, becomes the id, so "Money" and
 * "money" are one area rather than a built-in and a custom one.
 */
export const userAreaSchema = z
  .string()
  .transform(cleanUserArea)
  .pipe(z.string().min(1).max(USER_AREA_MAX_LENGTH))
  .transform((s) => builtInAreaIdFor(s) ?? s);

/**
 * The roles a routine or a skill suits: jobs, not job titles. A user can play
 * several, so okthx never stores the user's roles; the role is a filter on the
 * page and evidence for what okthx offers. An empty roles list on an item means
 * it suits every role.
 */
export const ROUTINE_ROLE_IDS = [
  "business-owner",
  "office-manager",
  "finance",
  "customer-service",
  "sales",
  "marketing",
  "consultant",
  "executive",
  "executive-assistant",
  "chief-of-staff",
  "founder",
  "venture-capital",
  "recruiting",
  "personal",
] as const;

export const routineRoleIdSchema = z.enum(ROUTINE_ROLE_IDS);
export type RoutineRoleId = z.infer<typeof routineRoleIdSchema>;

const ROUTINE_ROLE_NAMES: Record<RoutineRoleId, string> = {
  "business-owner": "Business owner",
  "office-manager": "Office manager",
  finance: "Finance",
  "customer-service": "Customer service",
  sales: "Sales",
  marketing: "Marketing",
  consultant: "Consultant",
  executive: "Executive",
  "executive-assistant": "Executive assistant",
  "chief-of-staff": "Chief of staff",
  founder: "Founder",
  "venture-capital": "Venture capital",
  recruiting: "Recruiting",
  personal: "Personal",
};

export const ROUTINE_ROLES: readonly { id: RoutineRoleId; name: string }[] = ROUTINE_ROLE_IDS.map(
  (id) => ({ id, name: ROUTINE_ROLE_NAMES[id] }),
);

/**
 * The roles on a user's own routine or skill: built-in role ids, at most one of
 * each. Duplicates are dropped, keeping the first, so the list can never be
 * longer than the role list.
 */
export const userRolesSchema = z
  .array(routineRoleIdSchema)
  .max(ROUTINE_ROLE_IDS.length)
  .transform((roles) => [...new Set(roles)]);
